import { parseLanguageCode } from "../../../i18n/utils";
import { RichText } from "../../cms/RichText/RichText";
import { locationSelectors } from "../../store/slice/locationSlice";
import Heading from "../ui/Heading";
import Paragraph from "../ui/Paragraph";
import { ExternalLinkButton } from "../ui/buttons/ExternalLinkButton";
import NotificationDialog from "../ui/dialogs/NotificationDialog";
import {
  getActionHelpTranslationKey,
  createTranslationKey,
  getActionTitleTranslationKey,
} from "./utils";
import { RegionalNamespaces } from "@pslifestyle/common/src/constants/translations";
import { Country } from "@pslifestyle/common/src/models/countries";
import { WholeAction } from "@pslifestyle/common/src/types/planTypes";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

type Props = {
  open: boolean;
  onClose: () => void;
  showHelpLinks?: boolean;
  action: WholeAction;
  country: Country;
};
export const ActionHelpModal = ({
  open,
  onClose,
  showHelpLinks,
  action,
  country,
}: Props) => {
  const language = useSelector(locationSelectors.language);
  const { t, i18n } = useTranslation([
    RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
    "common",
    "recommendations",
  ]);

  const languageCode = language
    ? parseLanguageCode(language).languageCode
    : country.defaultLanguage;

  const linksToShow =
    action.links?.filter((link) =>
      i18n.exists(createTranslationKey(link.linkTranslationKey, country.code), {
        ns: RegionalNamespaces.QUESTION_AND_RECOMMENDATIONS,
        /* links are only displayed based on the current language setting
          and should not fallback to english which is the application default */
        fallbackLng: language ?? undefined,
      }),
    ) ?? [];

  const renderActionHelpText = () => {
    if (action.localizedRichTextHelp) {
      return (
        <RichText
          className="relative self-start text-left"
          content={action.richTextHelp}
          localizedContent={action.localizedRichTextHelp}
          languageCode={languageCode}
        />
      );
    }
    return (
      <div className="relative self-start">
        {t(getActionHelpTranslationKey(action.id, country.code))
          .split("\n")
          .filter((str) => str)
          .map((str, index, self) => (
            <Paragraph
              type="body-lg"
              className={`py-1 w-full text-left ${!showHelpLinks && index === self.length - 1 ? "after:absolute after:inset-x-0 after:h-24 after:bottom-0 after:bg-gradient-to-t after:from-basic-white after:to-basic-white/0" : ""}`}
              // eslint-disable-next-line react/no-array-index-key
              key={`str${index}`}
            >
              {str}
            </Paragraph>
          ))}
      </div>
    );
  };
  return (
    <NotificationDialog
      open={open}
      onClose={onClose}
      closeButtonText={t("close", { ns: "common" })}
    >
      <Heading
        level={2}
        type="headline-sm-b"
        className="my-2 self-start text-left"
      >
        {t(getActionTitleTranslationKey(action.id, country.code))}
      </Heading>
      {renderActionHelpText()}
      {!showHelpLinks && (
        <div className="w-full pt-2">
          <Paragraph type="body-lg-b">
            {t("chooseThisForMoreInfo", { ns: "recommendations" })}
          </Paragraph>
        </div>
      )}
      {showHelpLinks && linksToShow.length > 0 && (
        <>
          <Paragraph type="body-lg-b" className="w-full text-left my-3">
            {t("helpfulLinks", { ns: "recommendations" })}:
          </Paragraph>
          <div className="flex flex-col w-full gap-2">
            {linksToShow.map((link) => (
              <ExternalLinkButton
                theme="secondary"
                linkOut
                key={link.link}
                url={link.link}
                text={`${t(createTranslationKey(link.linkTranslationKey, country.code))}`}
              />
            ))}
          </div>
        </>
      )}
    </NotificationDialog>
  );
};
